import React, { useState } from "react";
import { Truck, MapPin, PackageCheck, Hand, Undo2, Save, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "./api";

export const fulfilmentGroup = (o) => {
  const s = o?.stage || "";
  if (["completed", "delivered"].includes(s)) return "complete";
  if (["shipping", "quality_control"].includes(s) || (s === "delivery" && o?.delivery_status === "out")) return "on_the_way";
  return "open";
};
export const GROUP_LABEL = { open: "Open", on_the_way: "On the way", complete: "Complete" };

const btn = "inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.18em] uppercase font-semibold rounded disabled:opacity-50 disabled:cursor-not-allowed";

/** Fulfilment actions for one order. Saving tracking never marks the order sent; completion needs an explicit action. */
export function FulfilmentPanel({ order, onChanged }) {
  const [carrier, setCarrier] = useState(order.shipping_provider || "NZ Post");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [correcting, setCorrecting] = useState(false);
  const [reason, setReason] = useState("");
  const [emailing, setEmailing] = useState(false);
  const group = fulfilmentGroup(order);
  const dirty = tracking !== (order.tracking_number || "") || carrier !== (order.shipping_provider || "NZ Post");

  const run = async (action, extra = {}, ok = "Saved.") => {
    setBusy(action);
    try {
      const r = await api.post(`/admin/orders/${order.id}/fulfil`, { action, carrier, tracking_number: tracking, note, ...extra });
      if (r.data?.unchanged) toast.info("Nothing changed , the order was already in that state.");
      else toast.success(ok);
      onChanged && onChanged(r.data.order || r.data);
      setNote(""); setReason(""); setCorrecting(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't save , nothing was changed.");
    } finally { setBusy(""); }
  };

  const emailTracking = async () => {
    setEmailing(true);
    try { await api.post(`/console/orders/${order.id}/email-tracking`); toast.success("Tracking email sent."); onChanged && onChanged(); }
    catch { toast.error("Couldn't send the tracking email."); }
    finally { setEmailing(false); }
  };

  return (
    <div data-testid="fulfilment-panel" className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span data-testid="fulfilment-group" className={"px-2 py-1 rounded-full font-semibold tracking-[0.15em] uppercase text-[10px] " + (group === "complete" ? "bg-[#2F4A2A] text-white" : group === "on_the_way" ? "bg-[#1A73E8]/15 text-[#1A73E8]" : "bg-[#6E1E3A]/10 text-[#6E1E3A]")}>{GROUP_LABEL[group]}</span>
        <span className="text-[#7A6A55]">stage <strong>{order.stage}</strong>{order.fulfilment_method ? ` · ${order.fulfilment_method}` : ""}{order.tracking_number ? ` · ${order.shipping_provider || ""} ${order.tracking_number}` : ""}</span>
        {order.fulfilment_evidence && <span className="text-[#2F4A2A]">✓ {order.fulfilment_evidence.type} {new Date(order.fulfilment_evidence.at).toLocaleDateString("en-NZ")}{order.fulfilment_evidence.note ? ` · ${order.fulfilment_evidence.note}` : ""}</span>}
      </div>

      {group !== "complete" && (
        <>
          <div className="grid sm:grid-cols-[1fr_1.4fr_auto] gap-2 items-end bg-white border border-[#E8DFCB] rounded p-3">
            <label className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] font-semibold">Carrier
              <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="fulfil-carrier" className="mt-1 w-full px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded normal-case tracking-normal">
                {["NZ Post", "CourierPost", "Aramex", "NZ Couriers", "Post Haste", "Other"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] font-semibold">Tracking number
              <input value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="fulfil-tracking" placeholder="Not yet recorded" className="mt-1 w-full px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded normal-case tracking-normal" />
            </label>
            <button type="button" disabled={!dirty || !!busy} onClick={() => run("save_tracking", {}, "Tracking saved (order not marked sent).")} data-testid="fulfil-save-tracking" className={btn + " bg-white border border-[#3C2A1E] text-[#3C2A1E]"}>
              {busy === "save_tracking" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
            </button>
            <p className="sm:col-span-3 text-[11px] text-[#7A6A55] italic">{dirty ? "Unsaved tracking changes." : order.tracking_saved_at || order.tracking_number ? "Tracking recorded. It is not sent until you confirm below." : "No shipment recorded yet."}</p>
          </div>

          <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="fulfil-note" placeholder="Optional note for this action (e.g. left at back door, collected by partner)" className="w-full px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded" />

          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={!!busy || order.stage === "shipping"} onClick={() => run("confirm_sent", { sent_at: new Date().toISOString() }, "Marked as sent , now On the way.")} data-testid="fulfil-confirm-sent" className={btn + " bg-[#1A73E8] text-white"}>
              <Truck className="w-3.5 h-3.5" /> Confirm sent (courier)
            </button>
            <button type="button" disabled={!!busy || order.stage === "delivery"} onClick={() => run("add_to_delivery", {}, "Added to the local delivery queue.")} data-testid="fulfil-add-delivery" className={btn + " bg-[#6E1E3A] text-white"}>
              <MapPin className="w-3.5 h-3.5" /> Add to local delivery
            </button>
            <button type="button" disabled={!!busy} onClick={() => run("mark_delivered", {}, "Marked delivered , order complete.")} data-testid="fulfil-mark-delivered" className={btn + " bg-[#2F4A2A] text-white"}>
              <PackageCheck className="w-3.5 h-3.5" /> Mark delivered
            </button>
            <button type="button" disabled={!!busy} onClick={() => run("mark_collected", { collected_by: note }, "Marked collected , order complete.")} data-testid="fulfil-mark-collected" className={btn + " bg-[#3C2A1E] text-white"}>
              <Hand className="w-3.5 h-3.5" /> Mark collected
            </button>
            {order.email && (order.tracking_number || "").trim() && (
              <button type="button" onClick={emailTracking} disabled={emailing} data-testid="fulfil-email-tracking" className={btn + " bg-white border border-[#E8DFCB] text-[#6E1E3A]"}>
                <Mail className="w-3.5 h-3.5" /> {emailing ? "Sending…" : order.tracking_email_sent_at ? "Re-send tracking email" : "Email tracking"}
              </button>
            )}
          </div>
          <p className="text-[11px] text-[#7A6A55]">Delivered / collected finish this package only. Agreed cuttings, replacements or refunds stay open in the customer's orders view.</p>
        </>
      )}

      <div>
        {!correcting ? (
          <button type="button" onClick={() => setCorrecting(true)} data-testid="fulfil-correct-open" className="text-[11px] text-[#7A6A55] underline inline-flex items-center gap-1"><Undo2 className="w-3 h-3" /> Record a correction</button>
        ) : (
          <div className="bg-[#FBF6E9] border border-[#E8DFCB] rounded p-3 grid sm:grid-cols-[1fr_auto_auto] gap-2 items-end" data-testid="fulfil-correct-form">
            <label className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] font-semibold">Reason (recorded)
              <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="fulfil-correct-reason" className="mt-1 w-full px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded normal-case tracking-normal" />
            </label>
            <button type="button" disabled={!reason.trim() || !!busy} onClick={() => run("correct", { reason, revert_to_stage: "assigned" }, "Correction recorded , back to Open / packing.")} data-testid="fulfil-correct-submit" className={btn + " bg-[#A33030] text-white"}>Back to Open</button>
            <button type="button" onClick={() => setCorrecting(false)} className={btn + " bg-white border border-[#E8DFCB] text-[#7A6A55]"}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Structured hold: reason / owner / date / resolve. A blocking hold stops packing & dispatch server-side. */
export function HoldPanel({ order, onChanged }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [kind, setKind] = useState("refund_reconciliation");
  const [resolution, setResolution] = useState("");
  const [busy, setBusy] = useState(false);
  const hold = order.hold;
  const call = async (path, body, ok) => {
    setBusy(true);
    try { await api.post(`/admin/orders/${order.id}/hold${path}`, body); toast.success(ok); setOpen(false); setReason(""); setResolution(""); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't save."); }
    finally { setBusy(false); }
  };
  if (hold) {
    return (
      <div data-testid="order-hold-banner" className="border-2 border-[#A33030] bg-[#FFF4F0] rounded p-3 text-sm">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#A33030]">On hold · {String(hold.kind || "").replace(/_/g, " ")} · since {String(hold.since || "").slice(0, 10)}</div>
        <div className="mt-1 text-[#3C2A1E]">{hold.reason}</div>
        <div className="mt-2 grid sm:grid-cols-[1fr_auto] gap-2">
          <input value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder="How was it resolved? (recorded)" data-testid="hold-resolution" className="px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded" />
          <button type="button" disabled={busy || resolution.trim().length < 3} onClick={() => call("/resolve", { resolution }, "Hold resolved.")} data-testid="hold-resolve" className={btn + " bg-[#2F4A2A] text-white"}>Resolve hold</button>
        </div>
        <p className="text-[11px] text-[#A33030] mt-1">Packing, Confirm sent, delivery and Mark delivered are blocked while this hold is active. Nothing is refunded automatically.</p>
      </div>
    );
  }
  return (
    <div>
      {order.legacy_hold_note && !open && (
        <div data-testid="legacy-hold-note" className="mb-2 border-2 border-[#C49247] bg-[#FFF8E6] rounded p-3 text-sm">
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#A33030]">Hold wording in the notes · needs your decision</div>
          <div className="mt-1 text-[#3C2A1E] italic">“{order.legacy_hold_note}”</div>
          <p className="text-[11px] text-[#7A6A55] mt-1">Kept out of Ready to pack; packing and dispatch are blocked until you decide. The note itself is never changed. Nothing is refunded automatically.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={() => { setReason(order.legacy_hold_note.slice(0, 120)); setOpen(true); }} data-testid="legacy-hold-place" className={btn + " bg-[#A33030] text-white"}>Place a structured hold</button>
            <button type="button" disabled={busy} onClick={() => call("/clear-note", { decision: "cleared", note: "Reviewed , not an operational hold" }, "Note cleared , the order can be packed.")} data-testid="legacy-hold-clear" className={btn + " bg-white border border-[#E8DFCB] text-[#3C2A1E]"}>Clear , not a hold</button>
          </div>
        </div>
      )}
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid="hold-open" className="text-[11px] text-[#A33030] underline">Place a hold on this order</button>
      ) : (
        <div className="grid sm:grid-cols-[auto_1fr_auto_auto] gap-2 items-center bg-[#FFF4F0] border border-[#A33030]/40 rounded p-2" data-testid="hold-form">
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="hold-kind" className="px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded">
            {[["refund_reconciliation", "Refund reconciliation"], ["payment_review", "Payment review"], ["customer_clarification", "Awaiting customer"], ["stock", "Stock"], ["other", "Other"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (recorded with your name and today's date)" data-testid="hold-reason" className="px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded" />
          <button type="button" disabled={busy || reason.trim().length < 3} onClick={() => call("", { reason, kind, blocking: true }, "Hold placed , dispatch is blocked until resolved.")} data-testid="hold-submit" className={btn + " bg-[#A33030] text-white"}>Hold</button>
          <button type="button" onClick={() => setOpen(false)} className={btn + " bg-white border border-[#E8DFCB] text-[#7A6A55]"}>Cancel</button>
        </div>
      )}
    </div>
  );
}
